import React from 'react';
import { Link } from 'react-router-dom';
import { Cards, MagnifyingGlass, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import { Button, Empty } from '../../design-system/components/desktop';
import { useViewport } from '../../design-system/hooks/useViewport';
import { routes } from '../../routes/routes';
import { errorText, LOAD_FAILURE } from '../../utils/errorText';
import { Tile, TileGrid, TileGridSkeleton, TileIndexHeader, TileIndexSearch } from './tiles';

/**
 * The sets inside one game, the view. Archetype A10, second instance.
 *
 * Pure: the container (`TCGSetsPage.jsx`) owns both fetches, the catalog-group
 * de-duplication and the search state. This file owns what a buyer sees.
 *
 * NO LANGUAGE CHIPS, DELIBERATELY
 * -------------------------------
 * An earlier audit recorded a per-language chip filter here. The app removed
 * it: sets are language-agnostic, one `CardSet` per source ref, the fetch is
 * not scoped by language, and `TCGSetsPage.test.jsx` asserts that no
 * per-language filter button renders. The dead `.language-filter-btn` rules
 * went with the old stylesheet. Building chips here would be inventing a filter
 * the data no longer has.
 *
 * TWO FIELDS DELIBERATELY NOT SHOWN
 * ---------------------------------
 * `releaseDate` and `series` exist in the container's transform but are
 * hard-coded to `null` and never populated by the endpoint, so nothing here
 * renders a release date or a series line.
 *
 * FOUR STATES, NOT THREE
 * ----------------------
 * "No sets in this game" and "your search matched nothing" are different
 * problems with different ways out, so they are different empty states: the
 * first is the default tone, the second is `filtered` and its command clears
 * the search. An unknown `tcgId` is a fifth: it is not an empty result, it is a
 * URL that does not resolve, and the only way out is back to the games index.
 */

export function TCGSetsView({
  gameName,
  sets = [],
  loading = false,
  error = null,
  notFound = false,
  searchQuery = '',
  onSearchChange = () => {},
  /* This page's own path, so a set row can hand it to the filtered browse it
     opens. The view cannot read the route itself and stay pure. */
  selfPath,
}) {
  const { isDesktop } = useViewport();

  // The game is the leaf of the trail. Until it resolves there is no leaf, and
  // repeating "Sets" as its own child reads as a broken crumb rather than a
  // pending one.
  const trail = [
    { label: 'Marketplace', to: routes.MARKETPLACE },
    gameName ? { label: 'Sets', to: routes.SETS } : { label: 'Sets' },
  ];
  if (gameName) trail.push({ label: gameName });

  const shellProps = {
    activeNav: 'sets',
    breadcrumbs: isDesktop ? trail : [],
    back: { to: routes.SETS, label: 'Back to sets' },
  };

  // An unresolvable tcgId. There is nothing to search and nothing to filter, so
  // the page drops its controls entirely rather than offering a field that
  // cannot act on anything.
  if (notFound || (error && !gameName)) {
    return (
      <MarketShell {...shellProps}>
        <TileIndexHeader title="Sets" />
        <Empty
          /* A 404, not a failure: the game is simply not in the catalogue.
             `error` was the tone on one of four `not-found` call sites and is
             wrong on all of them. */
          icon={Cards}
          illustration="not-found"
          title="Trading card game not found"
          text={
            error ||
            'That game is not in the catalogue. It may have been renamed, or the link may be out of date.'
          }
          actions={
            <Button as={Link} to={routes.SETS}>
              Back to all games
            </Button>
          }
        />
      </MarketShell>
    );
  }

  let body;

  if (loading) {
    body = <TileGridSkeleton count={9} dense lines={1} />;
  } else if (error) {
    body = (
      <Empty
        tone="error"
        icon={WarningCircle}
        illustration="request-failed"
        title="We could not load these sets"
        text={errorText(error, LOAD_FAILURE)}
      />
    );
  } else if (sets.length === 0 && searchQuery) {
    body = (
      <Empty
        tone="filtered"
        icon={MagnifyingGlass}
        illustration="search-matched-nothing"
        title="No sets match that search"
        text="Nothing in this game is called "
        query={searchQuery}
        actions={
          <Button variant="ghost" onClick={() => onSearchChange('')}>
            Clear search
          </Button>
        }
      />
    );
  } else if (sets.length === 0) {
    body = (
      <Empty
        icon={Cards}
        illustration="nothing-catalogued-yet"
        title={`No sets found for ${gameName}`}
        text="Nothing has been catalogued for this game yet. Everything currently for sale is still browsable from the marketplace."
      />
    );
  } else {
    body = (
      <TileGrid dense>
        {sets.map((set) => (
          <Tile
            key={set.id}
            /* Same destination the page always used: the marketplace filtered
               by catalog group, which is the endpoint that actually holds the
               listings. */
            to={`${routes.MARKETPLACE}?catalog_group=${set.slug}`}
            /* THE TRAIL, handed over. The destination is browse wearing a
               filter, and browse renders no back control of its own — so
               without this a buyer three levels into /sets arrived somewhere
               that offered no way back up and did not name the set. */
            state={selfPath ? { from: selfPath, fromLabel: gameName } : undefined}
            image={set.logo || null}
            imageAlt={set.name}
            /* The app's own fallback: first two letters, uppercased. */
            placeholder={String(set.name || '').substring(0, 2).toUpperCase()}
            name={set.name}
            meta={`${set.cardCount} ${set.cardCount === 1 ? 'card' : 'cards'}`}
          />
        ))}
      </TileGrid>
    );
  }

  return (
    <MarketShell {...shellProps}>
      <TileIndexHeader title={gameName ? `${gameName} Sets` : 'Sets'} />
      <TileIndexSearch
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Search by sets"
        label={gameName ? `Search sets in ${gameName}` : 'Search sets'}
      />
      <div aria-busy={loading || undefined}>{body}</div>
    </MarketShell>
  );
}

export default TCGSetsView;
